/**
 * WalletManager - Gestión segura de wallets en memoria
 * Mantiene las claves privadas solo en memoria, nunca en storage
 */

import { ethers } from 'ethers';
import storageService from './storage.service';
import { appConfig } from '@/config/app';

class WalletManager {
  private static instance: WalletManager;
  private activeWallet: ethers.HDNodeWallet | ethers.Wallet | null = null;
  private lockTimer: NodeJS.Timeout | null = null;
  private readonly LOCK_TIMEOUT = appConfig.security.inactivityTimeout; // Configurable (defecto 15 min)
  // Contraseña de la sesión activa, solo en memoria — nunca a disco. Permite
  // cambiar/agregar wallets sin re-pedirla cada vez (mismo nivel de
  // exposición que activeWallet, que ya guarda la clave privada completa).
  private sessionPassword: string | null = null;

  private constructor() {
    // Singleton pattern
  }

  static getInstance(): WalletManager {
    if (!WalletManager.instance) {
      WalletManager.instance = new WalletManager();
    }
    return WalletManager.instance;
  }

  /**
   * Desbloquea la wallet con contraseña y la mantiene en memoria
   */
  async unlock(password: string): Promise<void> {
    try {
      console.log('[WalletManager] Desbloqueando wallet...');

      // Obtener datos encriptados desde localStorage
      const encryptedData = storageService.loadWalletData();
      if (!encryptedData || !encryptedData.privateKey) {
        throw new Error('No se encontró wallet encriptada');
      }

      // Desencriptar wallet
      const wallet = await ethers.Wallet.fromEncryptedJson(encryptedData.privateKey, password);
      console.log('[WalletManager] Wallet desencriptada con éxito');

      this.activeWallet = wallet;
      this.sessionPassword = password;

      // Iniciar timer de auto-lock
      this.resetLockTimer();

      console.log('[WalletManager] Wallet desbloqueada');
      
      // Emitir evento para que la UI se actualice
      document.dispatchEvent(new CustomEvent('wallet:unlocked', {
        detail: { address: this.activeWallet!.address }
      }));

    } catch (error) {
      console.error('[WalletManager] Error desbloqueando wallet:', error);
      this.activeWallet = null;
      throw error;
    }
  }

  loadFromPrivateKey(privateKey: string, mnemonic?: string): void {
    try {
      if (mnemonic && mnemonic.trim() !== '') {
        try {
          this.activeWallet = ethers.HDNodeWallet.fromPhrase(mnemonic.trim());
          console.log('[WalletManager] Wallet cargada desde sesión como HDNodeWallet con mnemónico');
        } catch (phrError) {
          console.warn('[WalletManager] Error al instanciar mnemónico, usando clave privada:', phrError);
          this.activeWallet = new ethers.Wallet(privateKey);
        }
      } else {
        this.activeWallet = new ethers.Wallet(privateKey);
        console.log('[WalletManager] Wallet cargada desde PK transferida (sin mnemónico)');
      }
      this.resetLockTimer();
      
      document.dispatchEvent(new CustomEvent('wallet:unlocked', {
        detail: { address: this.activeWallet.address }
      }));
    } catch (error) {
      console.error('[WalletManager] Error al cargar desde PK/Mnemonic:', error);
      this.activeWallet = null;
      throw error;
    }
  }

  /**
   * Obtiene la wallet activa (solo si está desbloqueada)
   */
  getActiveWallet(): ethers.HDNodeWallet | ethers.Wallet {
    if (!this.activeWallet) {
      throw new Error('Wallet está bloqueada. Desbloquea primero.');
    }
    
    // Extender timeout en cada uso
    this.resetLockTimer();
    
    return this.activeWallet;
  }

  /**
   * Obtiene la dirección de la wallet activa
   */
  getActiveAddress(): string | null {
    return this.activeWallet?.address || null;
  }

  /**
   * Contraseña de la sesión activa (solo en memoria) — null si está bloqueada
   * o si se desbloqueó por un camino sin contraseña (ej. PK transferida).
   */
  getSessionPassword(): string | null {
    return this.sessionPassword;
  }

  /**
   * Verifica si la wallet está desbloqueada
   */
  isUnlocked(): boolean {
    return this.activeWallet !== null;
  }

  /**
   * Bloquea la wallet (elimina de memoria)
   */
  lock(): void {
    // Eliminar wallet de memoria
    this.activeWallet = null;
    this.sessionPassword = null;

    // Limpiar timer
    if (this.lockTimer) {
      clearTimeout(this.lockTimer);
      this.lockTimer = null;
    }
    
    console.log('[WalletManager] Wallet bloqueada');
    
    // Emitir evento para que la UI se actualice
    document.dispatchEvent(new CustomEvent('wallet:locked'));
  }

  /**
   * Reinicia el timer de auto-lock
   */
  private resetLockTimer(): void {
    // Limpiar timer existente
    if (this.lockTimer) {
      clearTimeout(this.lockTimer);
    }
    
    // Crear nuevo timer
    this.lockTimer = setTimeout(() => {
      console.log('[WalletManager] Auto-lock por inactividad');
      this.lock();
    }, this.LOCK_TIMEOUT);
  }

  /**
   * Firma una transacción con la wallet activa
   */
  async signTransaction(transaction: any): Promise<string> {
    const wallet = this.getActiveWallet(); // Esto valida que esté desbloqueada
    return await wallet.signTransaction(transaction);
  }

  /**
   * Firma un mensaje con la wallet activa
   */
  async signMessage(message: string): Promise<string> {
    const wallet = this.getActiveWallet(); // Esto valida que esté desbloqueada
    return await wallet.signMessage(message);
  }

  /**
   * Obtiene la clave privada (solo para operaciones críticas)
   * ⚠️ USAR CON EXTREMA PRECAUCIÓN
   */
  getPrivateKey(): string {
    const wallet = this.getActiveWallet(); // Esto valida que esté desbloqueada
    return wallet.privateKey;
  }

  /**
   * Conecta la wallet a un provider
   */
  connectToProvider(provider: ethers.Provider): ethers.HDNodeWallet | ethers.Wallet {
    const wallet = this.getActiveWallet(); // Esto valida que esté desbloqueada
    return wallet.connect(provider);
  }

  /**
   * Extiende el tiempo de sesión (resetea el auto-lock)
   */
  extendSession(): void {
    if (this.isUnlocked()) {
      this.resetLockTimer();
    }
  }

  /**
   * Obtiene información básica de la wallet sin exponer la clave
   */
  getWalletInfo(): { address: string; isUnlocked: boolean } | null {
    if (!this.activeWallet) {
      return null;
    }

    return {
      address: this.activeWallet.address,
      isUnlocked: true
    };
  }
}

export default WalletManager;